import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Users, GitMerge, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { Navigate } from "react-router-dom";

// Admin dashboard: parallel-recurring-bill-chain sweep. Surfaces any
// (user, name) where two or more recurring chains exist side-by-side —
// the Apria/Google Storage pattern that quietly stacks duplicate charges
// in the Dashboard's monthly forecast. One-click Merge collapses the
// duplicates: keeps the biggest chain, deletes the others' unpaid rows,
// converts paid/cleared rows to is_recurring=False so history is preserved.
export default function AdminBillDuplicates() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [merging, setMerging] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/bills/duplicate-chains");
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const merge = async (group) => {
    const dupCount = Object.keys(group.duplicate_due_dates || {}).length;
    const confirmMsg = `Merge ${group.chain_count} parallel "${group.bill_name}" chains for ${group.user_email || group.user_id}?\n\n` +
      `• Total rows in play: ${group.total_rows}\n` +
      `• Duplicate due-dates: ${dupCount}\n\n` +
      `The largest chain will be kept. Unpaid rows from the others will be DELETED. Paid/cleared rows will be preserved as one-time history.`;
    if (!window.confirm(confirmMsg)) return;
    setMerging(`${group.user_id}::${group.bill_name}`);
    try {
      const { data } = await api.post("/admin/bills/merge-chains", {
        user_id: group.user_id,
        bill_name: group.bill_name,
      });
      toast.success(
        `Merged ${data.chains_merged} chain${data.chains_merged === 1 ? "" : "s"} · ` +
        `${data.unpaid_rows_deleted} deleted · ${data.paid_rows_converted_to_historical} historical`,
      );
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setMerging(null); }
  };

  const totalRows = useMemo(() => items.reduce((n, g) => n + g.total_rows, 0), [items]);

  if (!user) return <div className="text-[#59605C]">Loading…</div>;
  if (user.role !== "admin") return <Navigate to="/app/dashboard" replace />;

  return (
    <div className="space-y-6" data-testid="admin-bill-duplicates-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-[#8A918D]">Admin</div>
          <h1 className="font-heading text-3xl font-medium text-[#2C302E] mt-1">Duplicate bill chains</h1>
          <p className="text-[#59605C] mt-1 max-w-2xl text-sm">
            Every user + bill name where two or more parallel recurring chains
            exist side-by-side. Merging keeps the biggest chain, deletes unpaid
            rows from the others, and preserves paid/cleared rows as one-time
            history so audit trails don&rsquo;t disappear.
          </p>
        </div>
        <button className="btn-secondary" onClick={load} data-testid="admin-dup-refresh">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="text-[#59605C]" data-testid="admin-dup-loading">Scanning bills across every user…</div>
      ) : items.length === 0 ? (
        <div className="card-soft p-8 text-center bg-[#EEF3EC] border-[#B8CFB1]" data-testid="admin-dup-empty">
          <div className="font-heading text-lg text-[#2C302E]">No duplicate chains detected</div>
          <p className="text-sm text-[#59605C] mt-1">Every recurring bill in the database has a single canonical chain.</p>
        </div>
      ) : (
        <>
          <div className="card-soft p-4 bg-[#FEF3EE] border-[#F0C8B8] flex items-start gap-3" data-testid="admin-dup-summary">
            <AlertTriangle size={18} className="text-[#8C3A30] mt-0.5 shrink-0" />
            <div className="text-sm text-[#7A2E24]">
              <span className="font-semibold">{items.length} duplicate group{items.length === 1 ? "" : "s"}</span>{" "}
              across {totalRows} bill row{totalRows === 1 ? "" : "s"}. Review each before merging — the
              action deletes unpaid rows and is not undoable.
            </div>
          </div>

          <div className="card-soft overflow-hidden p-0">
            <table className="w-full text-sm">
              <thead className="bg-[#F2EFEB] text-[10px] font-semibold uppercase tracking-widest text-[#8A918D]">
                <tr>
                  <th className="text-left px-4 py-3">User</th>
                  <th className="text-left px-4 py-3">Bill name</th>
                  <th className="text-right px-4 py-3">Chains</th>
                  <th className="text-right px-4 py-3">Total rows</th>
                  <th className="text-right px-4 py-3">Dupe dates</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((g) => {
                  const key = `${g.user_id}::${g.bill_name}`;
                  const dupCount = Object.keys(g.duplicate_due_dates || {}).length;
                  return (
                    <tr key={key} className="border-t border-[#EBE6E0]" data-testid={`admin-dup-row-${g.user_id}`}>
                      <td className="px-4 py-3 min-w-[220px]">
                        <div className="flex items-center gap-2">
                          <Users size={14} className="text-[#8A918D]" />
                          <div>
                            <div className="text-[#2C302E] font-medium">{g.user_name || g.user_email || g.user_id.slice(0, 8)}</div>
                            <div className="text-[11px] text-[#8A918D]">{g.user_email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-[#2C302E]" data-testid={`admin-dup-name-${g.user_id}`}>{g.bill_name}</td>
                      <td className="px-4 py-3 text-right text-[#8C3A30] font-semibold">{g.chain_count}</td>
                      <td className="px-4 py-3 text-right text-[#59605C]">{g.total_rows}</td>
                      <td className="px-4 py-3 text-right">
                        {dupCount > 0 ? (
                          <span className="text-[#8C3A30]" title={Object.keys(g.duplicate_due_dates).join(", ")}>{dupCount}</span>
                        ) : (
                          <span className="text-[#8A918D]">0</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => merge(g)}
                          disabled={merging === key}
                          className="btn-primary !py-1.5 !px-3 text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
                          data-testid={`admin-dup-merge-${g.user_id}`}
                        >
                          <GitMerge size={13} /> {merging === key ? "Merging…" : "Merge chains"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
